import {
    ButtonGradientPrimary,
    COMPONENT_HEIGHTS,
    Icon,
    SearchBar,
    SelectFilled,
} from '@amelio/ui-library';
import { useAtomValue } from 'jotai';
import { useRef, useState } from 'react';

import { PlusIcon, SearchIcon } from './icons';

import type { CompetencyStatusFilter } from '../types';

import { userInstanceAtom } from '@/atoms/userDetails';
import { useAmelioTranslations } from '@/hooks';
import { useHandleClickOutside } from '@/hooks/utils/useHandleClickOutside';

type CompetenciesToolbarProps = {
    searchText: string;
    statusFilter: CompetencyStatusFilter;
    onSearchChange: (value: string) => void;
    onStatusFilterChange: (value: CompetencyStatusFilter) => void;
    onAddGroup: () => void;
    onAddCompetency: () => void;
    onExplore: () => void;
};

const STATUS_VALUES: CompetencyStatusFilter[] = ['all', 'active', 'inactive'];

export const CompetenciesToolbar = ({
    searchText,
    statusFilter,
    onSearchChange,
    onStatusFilterChange,
    onAddGroup,
    onAddCompetency,
    onExplore,
}: CompetenciesToolbarProps) => {
    const { t } = useAmelioTranslations();
    const isAmelioAdmin = useAtomValue(userInstanceAtom).isAmelioAdmin;

    const statusOptions = STATUS_VALUES.map((value) => ({
        value,
        label: t(`competencies.statusFilter.${value}`),
        startIcon:
            value === statusFilter ? (
                <Icon
                    name={'essential-checkmark'}
                    size={'sm'}
                    color={'inherit'}
                />
            ) : (
                <div className={'h-5 w-5'} />
            ),
    }));

    const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);
    const addMenuContainerRef = useRef<HTMLDivElement>(null);

    useHandleClickOutside({
        clicked: isAddMenuOpen,
        dropdownContainerRef: addMenuContainerRef,
        setButtonClicked: setIsAddMenuOpen,
    });

    return (
        <div className={'grid gap-4 lg:grid-cols-[1fr_auto] lg:items-end'}>
            <div
                className={
                    'grid gap-4 overflow-hidden sm:grid-cols-[16rem_minmax(0,1fr)] sm:items-end'
                }
            >
                <div className={'flex min-w-0 flex-col gap-3'}>
                    <label className={'text-navy text-sm font-medium'}>
                        {t('competencies.statusLabel')}
                    </label>

                    <SelectFilled
                        value={statusFilter}
                        options={statusOptions}
                        onChange={(value) => onStatusFilterChange(value as CompetencyStatusFilter)}
                        variant={'white'}
                        sort={'none'}
                        size={'md'}
                        dataTestId={'competencies-status-filter'}
                        className={'border-gray-200'}
                    />
                </div>

                <div className={'flex min-w-0 flex-col gap-3'}>
                    <SearchBar
                        value={searchText}
                        onChange={(value) => onSearchChange(value)}
                        placeholder={t('competencies.searchPlaceholder')}
                        size={'md'}
                        dataTestId={'competencies-search'}
                        className={'focus-within:!border-primary !border-gray-200'}
                    />
                </div>
            </div>

            <div
                className={
                    'flex w-full min-w-0 items-center gap-2 overflow-visible sm:!gap-4 lg:w-auto lg:min-w-fit lg:justify-end'
                }
            >
                {!isAmelioAdmin && (
                    <button
                        type={'button'}
                        onClick={onExplore}
                        className={`text-navy flex ${COMPONENT_HEIGHTS.md} min-w-0 flex-1 items-center justify-center gap-2 rounded-lg border-0 bg-white/90 text-sm leading-normal font-medium transition-colors hover:bg-gray-50 sm:flex-none sm:px-4`}
                        data-testid={'competencies-explore-button'}
                    >
                        <SearchIcon className={'h-4 w-4 flex-shrink-0 text-[#5E5E5E]'} />

                        <span className={'truncate'}>{t('competencies.exploreCompetencies')}</span>
                    </button>
                )}

                <div
                    className={'relative z-10 flex-shrink-0 overflow-visible'}
                    ref={addMenuContainerRef}
                >
                    <ButtonGradientPrimary
                        size={'md'}
                        onClick={() => setIsAddMenuOpen((previous) => !previous)}
                        leftIcon={<PlusIcon className={'h-5 w-5'} />}
                        dataTestId={'competencies-add-button'}
                        fitContent={true}
                    >
                        {t('competencies.add.button')}
                    </ButtonGradientPrimary>

                    {isAddMenuOpen && (
                        <div
                            className={
                                'absolute top-12 right-0 z-50 w-48 overflow-hidden rounded-lg border border-gray-100 bg-white py-1 shadow-lg sm:w-56'
                            }
                        >
                            <button
                                type={'button'}
                                onClick={() => {
                                    onAddCompetency();
                                    setIsAddMenuOpen(false);
                                }}
                                className={
                                    'text-navy block w-full px-4 py-2 text-left text-sm transition-colors hover:bg-gray-100'
                                }
                                data-testid={'competencies-add-competency'}
                            >
                                {t('competencies.add.competency')}
                            </button>

                            <button
                                type={'button'}
                                onClick={() => {
                                    onAddGroup();
                                    setIsAddMenuOpen(false);
                                }}
                                className={
                                    'text-navy block w-full px-4 py-2 text-left text-sm transition-colors hover:bg-gray-100'
                                }
                                data-testid={'competencies-add-group'}
                            >
                                {t('competencies.add.group')}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
